/** Labels follow physical positions in the browser's standard Gamepad mapping. */
export type ControllerFamily = 'playstation' | 'xbox' | 'nintendo' | 'generic';
export type ControllerGlyphs = 'auto' | ControllerFamily;
export type InputDevice =
  | { kind: 'keyboard' }
  | { kind: 'gamepad'; index: number; id: string; family: ControllerFamily };

export const KEYBOARD_DEVICE: InputDevice = { kind: 'keyboard' };
export const CONTROLLER_GLYPH_OPTIONS: readonly ControllerGlyphs[] = [
  'auto',
  'playstation',
  'xbox',
  'nintendo',
  'generic',
];

/** Browser IDs are intentionally unspecified: use recognizable names/vendor IDs, then fall back. */
export function detectControllerFamily(id: string): ControllerFamily {
  if (/dualsense|dualshock|playstation|\bps[345]\b|\b054c\b|vendor[:=\s]+0x054c/i.test(id))
    return 'playstation';
  if (/xbox|xinput|microsoft|\b045e\b|vendor[:=\s]+0x045e/i.test(id)) return 'xbox';
  if (/nintendo|joy[-\s]?con|switch|\b057e\b|vendor[:=\s]+0x057e/i.test(id)) return 'nintendo';
  return 'generic';
}

export function resolveControllerFamily(
  device: InputDevice,
  override: ControllerGlyphs = 'auto',
): ControllerFamily {
  if (override !== 'auto') return override;
  return device.kind === 'gamepad' ? device.family : 'generic';
}

const BUTTON_LABELS: Record<ControllerFamily, readonly string[]> = {
  playstation: [
    '× Cross',
    '○ Circle',
    '□ Square',
    '△ Triangle',
    'L1',
    'R1',
    'L2',
    'R2',
    'Create / Share',
    'Options',
    'L3',
    'R3',
    'D-pad ↑',
    'D-pad ↓',
    'D-pad ←',
    'D-pad →',
    'PS',
    'Touchpad',
  ],
  xbox: [
    'A',
    'B',
    'X',
    'Y',
    'LB',
    'RB',
    'LT',
    'RT',
    'View',
    'Menu',
    'LS click',
    'RS click',
    'D-pad ↑',
    'D-pad ↓',
    'D-pad ←',
    'D-pad →',
    'Xbox',
  ],
  nintendo: [
    'B',
    'A',
    'Y',
    'X',
    'L',
    'R',
    'ZL',
    'ZR',
    '−',
    '+',
    'Left stick click',
    'Right stick click',
    'D-pad ↑',
    'D-pad ↓',
    'D-pad ←',
    'D-pad →',
    'Home',
    'Capture',
  ],
  generic: [
    'South button',
    'East button',
    'West button',
    'North button',
    'Left bumper',
    'Right bumper',
    'Left trigger',
    'Right trigger',
    'Select',
    'Start',
    'Left stick click',
    'Right stick click',
    'D-pad ↑',
    'D-pad ↓',
    'D-pad ←',
    'D-pad →',
    'Home',
  ],
};

export function buttonLabel(
  index: number,
  device: InputDevice,
  override: ControllerGlyphs = 'auto',
): string {
  const family = resolveControllerFamily(device, override);
  if (family === 'playstation' && index === 8 && device.kind === 'gamepad') {
    if (/dualsense|\bps5\b|\b0ce6\b|\b0df2\b/i.test(device.id)) return 'Create';
    if (/dualshock|\bps4\b|\b05c4\b|\b09cc\b/i.test(device.id)) return 'Share';
  }
  return BUTTON_LABELS[family][index] ?? `Button ${index + 1}`;
}

export function deviceLabel(device: InputDevice, override: ControllerGlyphs = 'auto'): string {
  if (device.kind === 'keyboard') return 'Keyboard & mouse';
  const family = resolveControllerFamily(device, override);
  if (family === 'playstation') {
    if (/dualsense|\bps5\b|\b0ce6\b|\b0df2\b/i.test(device.id)) return 'DualSense';
    if (/dualshock|\bps4\b|\b05c4\b|\b09cc\b/i.test(device.id)) return 'DualShock';
    return 'PlayStation controller';
  }
  return { xbox: 'Xbox controller', nintendo: 'Nintendo controller', generic: 'Controller' }[
    family
  ];
}

type PadSnapshot = { id: string; buttons: boolean[]; axes: number[] };
type PresentationPad = Pick<
  Gamepad,
  'id' | 'index' | 'buttons' | 'axes' | 'connected' | 'timestamp'
>;

export type InputTracker = {
  readonly device: InputDevice;
  readonly revision: number;
  /** Returns true only when the displayed input device changed. */
  update(pads: readonly (PresentationPad | null | undefined)[]): boolean;
  /** Keeps pad held-state: a held stick/button must not immediately steal focus back. */
  useKeyboard(): boolean;
};

/** Meaningful new input wins; connection, touch sensors, releases and idle stick drift do not. */
export function createInputTracker(): InputTracker {
  let device: InputDevice = KEYBOARD_DEVICE;
  let revision = 0;
  const snapshots = new Map<number, PadSnapshot>();
  const changeDevice = (next: InputDevice) => {
    if (
      device.kind === next.kind &&
      (device.kind === 'keyboard' ||
        (next.kind === 'gamepad' && device.index === next.index && device.id === next.id))
    )
      return false;
    device = next;
    revision++;
    return true;
  };
  return {
    get device() {
      return device;
    },
    get revision() {
      return revision;
    },
    useKeyboard() {
      return changeDevice(KEYBOARD_DEVICE);
    },
    update(pads) {
      const connected = new Map<number, PresentationPad>();
      let candidate: PresentationPad | undefined;
      for (const pad of pads) {
        if (!pad || !pad.connected) continue;
        connected.set(pad.index, pad);
        const saved = snapshots.get(pad.index);
        const previous = saved?.id === pad.id ? saved : undefined;
        const buttons = pad.buttons.map((button) => button.pressed || button.value > 0.55);
        const axes = pad.axes.slice(0, 4).map((value, index) => {
          const old = previous?.axes[index] ?? 0;
          // Engage deliberately; keep the same direction through the hysteresis band.
          if (Math.abs(value) >= 0.6) return Math.sign(value);
          if (Math.abs(value) > 0.35 && Math.sign(value) === old) return old;
          return 0;
        });
        const freshButton = buttons.some((down, index) => down && !previous?.buttons[index]);
        const freshAxis = axes.some(
          (direction, index) => direction !== 0 && direction !== (previous?.axes[index] ?? 0),
        );
        if ((freshButton || freshAxis) && (!candidate || pad.timestamp >= candidate.timestamp))
          candidate = pad;
        snapshots.set(pad.index, { id: pad.id, buttons, axes });
      }
      for (const index of snapshots.keys()) {
        if (!connected.has(index)) snapshots.delete(index);
      }
      if (candidate) {
        return changeDevice({
          kind: 'gamepad',
          index: candidate.index,
          id: candidate.id,
          family: detectControllerFamily(candidate.id),
        });
      }
      if (device.kind === 'gamepad' && connected.get(device.index)?.id !== device.id)
        return changeDevice(KEYBOARD_DEVICE);
      return false;
    },
  };
}
